import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cachedGet } from '../../lib/api-client';
import { onTripRefresh } from '../../lib/trip-refresh';
import { Spinner } from '../../components/kit/Spinner';
import { EmptyState } from '../../components/kit/EmptyState';
import { ErrorState } from '../../components/kit/ErrorState';
import { StaleBanner } from '../../components/app/StaleBanner';
import { TripCard } from '../../components/home/TripCard';
import { useAuth } from '../../lib/auth';
import { haptic } from '../../lib/haptic';

type Trip = {
  id: string;
  code?: string | null;
  pickupTime: string;
  pickupLocation?: string | null;
  dropLocation?: string | null;
  status: string;
  client?: { name: string } | null;
  isVip?: boolean;
};

/** Pick a time-of-day greeting. Browser-local time is the correct signal here —
 *  the "no Date.getHours()" rule lives in apps/api (which must use IST helpers),
 *  not in this client-side React app. */
function greetingKey(now = new Date()): 'morning' | 'afternoon' | 'evening' {
  const h = now.getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}

type ShortcutTileProps = {
  to: string;
  label: string;
  icon: 'schedule' | 'history' | 'vehicle' | 'help';
};

function ShortcutTile({ to, label, icon }: ShortcutTileProps) {
  return (
    <Link
      to={to}
      onClick={() => haptic.tap()}
      className="flex flex-col items-center justify-center gap-2 p-3 rounded-[14px] bg-[var(--color-surface)] border border-[var(--color-border)] hover:border-[var(--color-border-strong,#C9C6BA)] transition-colors"
    >
      <div
        className="flex items-center justify-center rounded-[10px]"
        style={{
          width: 36,
          height: 36,
          background: 'var(--mint-soft)',
          color: 'var(--green-700)',
        }}
      >
        <ShortcutIcon kind={icon} />
      </div>
      <span className="text-xs font-semibold text-[var(--color-text)] text-center leading-tight">
        {label}
      </span>
    </Link>
  );
}

function ShortcutIcon({ kind }: { kind: ShortcutTileProps['icon'] }) {
  const common = {
    width: 18,
    height: 18,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  if (kind === 'schedule') {
    return (
      <svg {...common} aria-hidden>
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    );
  }
  if (kind === 'history') {
    return (
      <svg {...common} aria-hidden>
        <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
        <polyline points="3 3 3 8 8 8" />
        <line x1="12" y1="7" x2="12" y2="12" />
        <line x1="12" y1="12" x2="15" y2="14" />
      </svg>
    );
  }
  if (kind === 'vehicle') {
    return (
      <svg {...common} aria-hidden>
        <rect x="2" y="11" width="20" height="7" rx="1" />
        <path d="M5 11V8a3 3 0 0 1 3-3h8a3 3 0 0 1 3 3v3" />
        <circle cx="7" cy="18" r="1.5" />
        <circle cx="17" cy="18" r="1.5" />
      </svg>
    );
  }
  // help
  return (
    <svg {...common} aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

export function HomeRoute() {
  const { t } = useTranslation('home');
  const driver = useAuth(s => s.driver);
  const [loading, setLoading] = useState(true);
  const [trips, setTrips] = useState<Trip[]>([]);
  const [fetchedAt, setFetchedAt] = useState<number | null>(null);
  const [stale, setStale] = useState(false);
  const [error, setError] = useState(false);

  const load = async (forceNetwork = false) => {
    setError(false);
    setLoading(true);
    try {
      const result = await cachedGet<{ items: Trip[] }>('/driver-app/me/trips/today', { forceNetwork });
      setTrips(result.data.items);
      setFetchedAt(Date.now());
      setStale(result.state === 'stale');
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  useEffect(() => {
    const refresh = () => { if (document.visibilityState === 'visible') void load(true); };
    const unsubscribe = onTripRefresh(refresh);
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    const timer = window.setInterval(refresh, 15_000);
    return () => {
      unsubscribe();
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
      window.clearInterval(timer);
    };
  }, []);

  if (loading && trips.length === 0) return <div className="flex justify-center p-8"><Spinner size={32} /></div>;
  if (error && trips.length === 0) return <ErrorState onRetry={load} />;

  const firstName = driver?.firstName || driver?.name || 'driver';
  const greeting = t(`greeting.${greetingKey()}`);
  // 2026-07-02: no more "Next trip" vs "Later today" buckets. The active duty
  // (live or awaiting close-out) pins to the top; everything else lists
  // chronologically under "Upcoming". The backend already excludes terminal
  // trips (completed/cancelled) from the today feed and sorts by pickupTime.
  const ACTIVE_STATUSES = new Set(['DISPATCHED', 'ONGOING', 'RELEASED']);
  const activeTrips = trips.filter(tr => ACTIVE_STATUSES.has((tr.status || '').toUpperCase()));
  const otherTrips = trips.filter(tr => !ACTIVE_STATUSES.has((tr.status || '').toUpperCase()));
  const primaryTrip = activeTrips[0] ?? otherTrips[0];
  const primaryIsActive = activeTrips.length > 0;
  const upcomingTrips = [...activeTrips.slice(1), ...otherTrips.filter(tr => tr !== primaryTrip)];

  return (
    <div>
      {stale && fetchedAt ? <StaleBanner fetchedAt={fetchedAt} onRefresh={() => load(true)} /> : null}

      <div className="px-5 pt-2" style={{ paddingBottom: 'calc(96px + env(safe-area-inset-bottom))' }}>
        {/* Greeting block — eyebrow is intentionally omitted for PR-2.5
            because DriverProfile doesn't carry an operator/company name yet.
            v2 design accommodates greeting-only headers. */}
        <h1 className="text-[26px] font-extrabold tracking-tight leading-[1.15] text-[var(--color-text)]">
          {greeting}, <span className="serif-accent">{firstName}</span>.
        </h1>

        {/* Stat card (mint-soft) */}
        <div
          className="mt-5 px-[18px] py-4 rounded-[var(--radius-card)]"
          style={{
            background: 'var(--mint-soft)',
            border: '1px solid var(--mint-line)',
          }}
        >
          <div
            className="text-[11px] font-bold uppercase"
            style={{ color: 'var(--green-700)', letterSpacing: '0.08em' }}
          >
            {t('tripsLeftLabel')}
          </div>
          <div className="mt-1 flex items-baseline gap-1.5 text-[var(--color-text)]">
            <span className="serif-accent text-[34px] leading-none">
              {trips.length}
            </span>
            <span className="text-sm font-semibold text-[var(--color-text-secondary)]">
              {t('tripsLeftSuffix', { count: trips.length })}
            </span>
          </div>
        </div>

        {/* Alert area slot — intentionally empty for PR-2.5 (no real notification
            data yet). Wired in PR-4. */}

        {trips.length === 0 ? (
          <div className="mt-6">
            <EmptyState title={t('noTripsToday')} subtitle={t('noTripsTodaySubtitle')} />
          </div>
        ) : (
          <>
            {/* Primary duty (accent) — the live trip when one exists,
                otherwise the next chronological booking. */}
            {primaryTrip ? (
              <>
                <div className="flex items-center justify-between mt-6 mb-3">
                  <h2
                    className="text-[13px] font-bold uppercase"
                    style={{ color: 'var(--color-text-dim,#9AA3AC)', letterSpacing: '0.08em' }}
                  >
                    {primaryIsActive
                      ? t('currentDuty', { defaultValue: 'Current duty' })
                      : t('nextDuty', { defaultValue: 'Next duty' })}
                  </h2>
                </div>
                <ul>
                  <TripCard trip={primaryTrip} accent isNext />
                </ul>
              </>
            ) : null}

            {/* Everything else, chronological — no "later today" bucket. */}
            {upcomingTrips.length > 0 ? (
              <>
                <div className="flex items-center justify-between mt-6 mb-3">
                  <h2
                    className="text-[13px] font-bold uppercase"
                    style={{ color: 'var(--color-text-dim,#9AA3AC)', letterSpacing: '0.08em' }}
                  >
                    {t('upcoming', { defaultValue: 'Upcoming' })}
                  </h2>
                </div>
                <ul className="space-y-3">
                  {upcomingTrips.map(trip => (
                    <TripCard key={trip.id} trip={trip} />
                  ))}
                </ul>
              </>
            ) : null}
          </>
        )}

      </div>

      {/* Bottom-stuck shortcut bar — 4 quick-access tiles fixed to the
          viewport bottom. Content above gets a bottom padding to clear
          the bar (set via padding-bottom on the page wrapper). Safe-area
          inset accounts for iOS home indicator. */}
      <div
        className="fixed z-30 grid grid-cols-4 gap-2 px-5 w-full"
        style={{
          // Pin to the 440px column on desktop (left:50% + translate +
          // max-width matches the mobile-frame layout). On mobile the
          // column is the viewport so this is identical to left-0/right-0.
          bottom: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          maxWidth: 440,
          paddingTop: 12,
          paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
          // Solid page-color bg so the shortcuts stay legible over any
          // scrolling content underneath.
          background: 'var(--color-bg-page)',
        }}
      >
        <ShortcutTile to="/today" label={t('shortcuts.schedule')} icon="schedule" />
        <ShortcutTile to="/trips/history" label={t('shortcuts.history')} icon="history" />
        <ShortcutTile to="/vehicle" label={t('shortcuts.vehicle')} icon="vehicle" />
        <ShortcutTile to="/help" label={t('shortcuts.help')} icon="help" />
      </div>
    </div>
  );
}
